import { acceptHMRUpdate, defineStore } from "pinia";
import { markRaw, ref, type Component, type Ref } from "vue";

export type ModalType = "alert" | "confirm" | "prompt" | "multiline-prompt";

export interface BuiltinModal {
  kind: "builtin";
  title: string;
  message: string;
  type: ModalType;
  confirmText?: string;
  cancelText?: string;
  titleIcon?: string;
  promptedValueName?: string;
  response?: string;
  onConfirm: () => void;
  onCancel: () => void;
  inputType?: string;
  inputPattern?: string;
  isDangerous?: boolean;
}

export interface ComponentModal {
  kind: "component";
  title: string;
  titleIcon?: string;
  body: Component;
  bodyProps?: Record<string, unknown>;
  models?: Record<string, Ref<unknown>>;
  footer?: Component;
  footerProps?: Record<string, unknown>;
  close: (result: unknown) => void;
  cancel: () => void;
}

export type Modal = BuiltinModal | ComponentModal;

export const useModalsStore = defineStore("modals", () => {
  const stack = ref<Modal[]>([]);

  /**
   * Show an alert modal.
   *
   * Usage:
   *
   * ```ts
   * if (await alert("title", "message")) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param confirmText The text of the confirm button.
   * @returns A promise that resolves to true if the user confirms the modal, false otherwise.
   */
  async function alert({
    title,
    titleIcon = "",
    message,
    confirmText = "OK",
  }: {
    title: string;
    titleIcon?: string;
    message: string;
    confirmText?: string;
  }) {
    return new Promise<void>((resolve) => {
      function handleConfirm() {
        stack.value.shift();
        resolve();
      }

      stack.value.push({
        kind: "builtin",
        title,
        titleIcon,
        message,
        type: "alert",
        confirmText,
        onConfirm: () => handleConfirm(),
        onCancel: () => handleConfirm(),
      });
    });
  }

  /**
   * Show a confirm modal.
   *
   * Usage:
   *
   * ```ts
   * if (await confirm("title", "message")) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param confirmText The text of the confirm button.
   * @param cancelText The text of the cancel button.
   * @returns A promise that resolves to true if the user confirms the modal, false otherwise.
   */
  async function confirm({
    title,
    titleIcon = "",
    message,
    confirmText = "OK",
    cancelText = "Cancel",
    isDangerous = false,
  }: {
    title: string;
    titleIcon?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDangerous?: boolean;
  }) {
    return new Promise<boolean>((resolve) => {
      function handleConfirm(isConfirmed: boolean) {
        stack.value.shift();
        resolve(isConfirmed);
      }

      stack.value.push({
        kind: "builtin",
        title,
        titleIcon,
        message,
        type: "confirm",
        confirmText,
        cancelText,
        onConfirm: () => handleConfirm(true),
        onCancel: () => handleConfirm(false),
        isDangerous,
      });
    });
  }

  /**
   * Show a prompt modal.
   *
   * Usage:
   *
   * ```ts
   * const response = await prompt("title", "message", "promptedValueName");
   * if (response) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param promptedValueName The name of the prompted value.
   * @param confirmText The text of the confirm button.
   * @param cancelText The text of the cancel button.
   * @returns A promise that resolves to the prompted value.
   */
  async function prompt({
    title,
    titleIcon = "",
    message = "",
    promptedValueName = "",
    confirmText = "OK",
    cancelText = "Cancel",
    inputType = undefined,
    inputPattern = undefined,
    isDangerous = false,
  }: {
    title: string;
    titleIcon?: string;
    message?: string;
    promptedValueName?: string;
    confirmText?: string;
    cancelText?: string;
    inputType?: string;
    inputPattern?: string;
    isDangerous?: boolean;
  }) {
    return _prompt(
      title,
      titleIcon,
      message,
      promptedValueName,
      confirmText,
      cancelText,
      false,
      inputType,
      inputPattern,
      isDangerous
    );
  }

  /**
   * Show a prompt modal.
   *
   * Usage:
   *
   * ```ts
   * const response = await multilinePrompt("title", "message", "promptedValueName");
   * if (response) {
   *   // do something
   * }
   * ```
   *
   * @param title The title of the modal.
   * @param message The message of the modal.
   * @param promptedValueName The name of the prompted value.
   * @param confirmText The text of the confirm button.
   * @param cancelText The text of the cancel button.
   * @returns A promise that resolves to the prompted value.
   */
  async function multilinePrompt({
    title,
    titleIcon = "",
    message = "",
    promptedValueName = "",
    confirmText = "OK",
    cancelText = "Cancel",
  }: {
    title: string;
    titleIcon?: string;
    message?: string;
    promptedValueName?: string;
    confirmText?: string;
    cancelText?: string;
  }) {
    return _prompt(title, titleIcon, message, promptedValueName, confirmText, cancelText, true);
  }

  function _prompt(
    title: string,
    titleIcon: string,
    message: string,
    promptedValueName: string,
    confirmText: string,
    cancelText: string,
    isMultiline: boolean,
    inputType?: string,
    inputPattern?: string,
    isDangerous?: boolean
  ) {
    return new Promise<[boolean, string]>((resolve) => {
      function handleConfirm(confirmed: boolean) {
        const m = stack.value.shift();
        resolve([confirmed, m?.kind === "builtin" ? (m.response ?? "") : ""]);
      }

      stack.value.push({
        kind: "builtin",
        title,
        titleIcon,
        message,
        type: isMultiline ? "multiline-prompt" : "prompt",
        promptedValueName,
        confirmText,
        cancelText,
        onConfirm: () => handleConfirm(true),
        onCancel: () => handleConfirm(false),
        inputType,
        inputPattern,
        isDangerous,
      });
    });
  }

  /**
   * Open a component-backed modal rendered inside the global `ModalDialog` shell.
   *
   * Usage:
   *
   * ```ts
   * import MyModalBody from "@/components/modals/MyModalBody.vue";
   * import MyModalFooter from "@/components/modals/MyModalFooter.vue";
   * import { ref } from "vue";
   *
   * const name = ref("");
   * const result = await openComponentModal<string>({
   *   title: "Rename",
   *   body: MyModalBody,
   *   footer: MyModalFooter,
   *   models: { name }, // modal content can use defineModel("name")
   * });
   * ```
   *
   * Notes:
   * - `close(result)` and `cancel()` are injected as props into both `body` and `footer`.
   * - Prefer `models` (refs) for editable state so modal content can use `defineModel(...)`
   *   rather than mutating props.
   *
   * @param title The title of the modal.
   * @param titleIcon An optional icon class shown next to the title.
   * @param body The component rendered in the modal content area.
   * @param bodyProps Optional extra props forwarded to the body component.
   * @param models Optional `v-model` bindings (refs) forwarded to body/footer.
   * @param footer Optional component rendered in the modal footer/actions area.
   * @param footerProps Optional extra props forwarded to the footer component.
   * @returns A promise that resolves with the value passed to `close(result)`, or `undefined` if
   * the modal is cancelled (Escape / close button / `cancel()`).
   */
  async function openComponentModal<TResult = unknown>({
    title,
    titleIcon = "",
    body,
    bodyProps,
    models,
    footer,
    footerProps,
  }: {
    title: string;
    titleIcon?: string;
    body: Component;
    bodyProps?: Record<string, unknown>;
    models?: Record<string, Ref<unknown>>;
    footer?: Component;
    footerProps?: Record<string, unknown>;
  }) {
    return new Promise<[boolean, TResult | undefined]>((resolve) => {
      function close(result: unknown) {
        stack.value.shift();
        resolve([true, result as TResult]);
      }

      function cancel() {
        stack.value.shift();
        resolve([false, undefined]);
      }

      stack.value.push({
        kind: "component",
        title,
        titleIcon,
        body: markRaw(body),
        bodyProps,
        models: models ? markRaw(models) : undefined,
        footer: footer ? markRaw(footer) : undefined,
        footerProps,
        close,
        cancel,
      });
    });
  }

  /**
   * Get the current modal in the stack.
   * @returns The current modal.
   */
  function getCurrentModal() {
    return stack.value[0];
  }

  return { alert, confirm, prompt, multilinePrompt, openComponentModal, getCurrentModal };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useModalsStore, import.meta.hot));
}
